import {
  type OnInit,
  effect,
  DestroyRef,
  Component,
  forwardRef,
  inject,
  input,
  computed,
} from '@angular/core';
import {
  ControlErrorComponent,
  type ControlValueAccessorOnChange,
  type ControlValueAccessorOnTouched,
} from '@pct-ng/form';
import type { ControlValueAccessor } from '@angular/forms';
import {
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import type { AbstractFileUploadService } from '@pct-ng/feature-file-upload-native';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { I18nPrefixConcatPipe } from '@pct-ng/i18n';
import { TranslocoPipe } from '@jsverse/transloco';
import { type CoverImagePutDTO, type LanguageCode } from '@cappus/shared-common';
import { MaterialFileUploadComponent } from '@pct-ng/feature-file-upload-material';
import { COVER_IMAGE_CONFIG_INJECTION_TOKEN } from './config-injection-token/cover-image-config-injection-token';

@Component({
  selector: 'cappus-cover-image',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatFormField,
    ControlErrorComponent,
    TranslocoPipe,
    I18nPrefixConcatPipe,
    MaterialFileUploadComponent,
  ],
  templateUrl: './cover-image.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoverImageComponent),
      multi: true,
    },
  ],
})
export class CoverImageComponent implements ControlValueAccessor, OnInit {
  readonly i18nPrefix = input.required<string>();
  readonly #destroyRef = inject(DestroyRef);
  readonly selectedLanguage = input.required<LanguageCode>();
  readonly startedLanguages = input.required<LanguageCode[]>();
  readonly fileUploadService = input.required<AbstractFileUploadService<any>>();
  readonly isSubtitleRequired = input.required<boolean>();
  readonly coverImageConfig = inject(COVER_IMAGE_CONFIG_INJECTION_TOKEN);
  readonly parentFormSubmitted = input.required<boolean>();

  #onChange!: ControlValueAccessorOnChange<CoverImagePutDTO | null>;
  #onTouched!: ControlValueAccessorOnTouched;

  protected readonly idControl = new FormControl<CoverImagePutDTO['id']>('', {
    validators: Validators.required,
    nonNullable: true,
  });

  protected readonly subtitleControl = new FormControl<CoverImagePutDTO['subtitle']>('', {
    validators: [Validators.maxLength(255)],
    nonNullable: true,
  });

  protected readonly form = new FormGroup({
    id: this.idControl,
    subtitle: this.subtitleControl,
  });

  constructor() {
    this.setSubtitleRequired();
  }

  protected readonly selectedLanguageIsStarted = computed(() =>
    this.startedLanguages().includes(this.selectedLanguage()),
  );

  protected readonly noLanguageStarted = computed(() => this.startedLanguages().length === 0);

  setSubtitleRequired(): void {
    effect(() => {
      const selectedLanguageIsStarted = this.selectedLanguageIsStarted();
      if (selectedLanguageIsStarted) {
        this.idControl.addValidators(Validators.required);
      } else {
        this.idControl.removeValidators(Validators.required);
      }
      this.idControl.updateValueAndValidity({ emitEvent: false });
    });
    effect(() => {
      const isSubtitleRequired = this.isSubtitleRequired();
      const selectedLanguageIsStarted = this.selectedLanguageIsStarted();
      const noLanguageStarted = this.noLanguageStarted();
      if (isSubtitleRequired && (selectedLanguageIsStarted || noLanguageStarted)) {
        this.subtitleControl.addValidators(Validators.required);
      } else {
        this.subtitleControl.removeValidators(Validators.required);
      }
      this.subtitleControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(value => {
      this.#onTouched?.();
      this.#onChange?.(this.form.valid ? (value as CoverImagePutDTO) : null);
    });
  }

  writeValue(value: CoverImagePutDTO | null): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    } else {
      this.form.reset(undefined, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  markAsTouched(): void {
    this.#onTouched();
  }
}
